/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Typography } from "@mui/material";
import { ScrapEyeLayoutMap } from "components/scrapEyeLayout/ScrapEyeLayoutMap";
import { MaterialHotkeyOverlay } from "components/scrapEyeLayout/hotkeys/MaterialHotkeyOverlay";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterials } from "hooks/useMaterials";
import { useEffect, useMemo, useRef, useState } from "react";
import { useScrapLoadingLayout } from "../hooks/useScrapLoadingLayout";

const ScaleToFit = ({ children }: { children: React.ReactNode }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const update = () => {
      const outerWidth = outer.clientWidth;
      const innerWidth = inner.scrollWidth;
      const innerHeight = inner.scrollHeight;
      if (innerWidth === 0) return;
      const newScale = Math.min(1, outerWidth / innerWidth);
      setScale(newScale);
      setScaledHeight(innerHeight * newScale);
    };

    const observer = new ResizeObserver(update);
    observer.observe(outer);
    observer.observe(inner);
    update();
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      ref={outerRef}
      sx={{ overflow: "hidden", width: "100%", height: scaledHeight }}
    >
      <Box
        ref={innerRef}
        sx={{
          width: "max-content",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface LabellingOverlaysProps {
  isShiftHeld: boolean;
  areaColors: Record<string, string>;
  isAreaModeActive?: boolean;
  binEntryActive?: boolean;
  binBuffer?: string;
  binEntryAvailable?: boolean;
  areaMaterials?: Record<string, string>;
  location?: string;
}

export const LabellingOverlays = ({
  isShiftHeld,
  areaColors,
  isAreaModeActive = false,
  binEntryActive = false,
  binBuffer = "",
  binEntryAvailable = false,
  areaMaterials,
  location = "crane",
}: LabellingOverlaysProps) => {
  const { t } = useTenantTranslation();
  const { layout, layoutType } = useScrapLoadingLayout(location);
  const { materials } = useMaterials();

  const filteredMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.labelling_name !== null) ?? []
    );
  }, [materials]);

  return (
    <>
      {isAreaModeActive && layout != null && layoutType != null ? (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: 16,
            maxWidth: "calc(100% - 32px)",
            overflow: "hidden",
            opacity: 0.95,
            zIndex: 1000,
            bgcolor: "background.paper",
            p: 1,
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          {binEntryAvailable ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 0.5,
                fontWeight: binEntryActive ? "bold" : "normal",
                color: binEntryActive ? "primary.main" : "text.secondary",
              }}
            >
              {binEntryActive
                ? t("binNumberEntrySelecting", { buffer: binBuffer })
                : t("binNumberEntryHint")}
            </Typography>
          ) : null}
          <ScaleToFit>
            <ScrapEyeLayoutMap
              areaColors={areaColors}
              areaMaterials={areaMaterials}
              location={location}
            />
          </ScaleToFit>
        </Box>
      ) : null}

      {isShiftHeld ? (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            overflow: "hidden",
            opacity: 0.95,
            zIndex: 1000,
            bgcolor: "background.paper",
            p: 1,
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <MaterialHotkeyOverlay materials={filteredMaterials} />
        </Box>
      ) : null}
    </>
  );
};
